import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, HardDrive, FileStack, Folder, TrendingUp, Loader2, Star, Clock } from 'lucide-react';
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip, BarChart, Bar, XAxis,
    YAxis, CartesianGrid, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { api, fmtBytes, fmtDate, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const CHART_COLORS = ['#0044FF', '#7C3AED', '#F97316', '#10B981', '#EC4899', '#EAB308', '#06B6D4', '#8B5CF6', '#22C55E', '#F43F5E'];

function StatCard({ label, value, sub, icon: Icon }) {
    return (
        <div className="rounded-sm border border-border bg-card p-5">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{label}</div>
                <Icon size={14} className="text-muted-foreground" />
            </div>
            <div className="mt-3 font-display text-3xl font-black tracking-tight">{value}</div>
            {sub && <div className="mt-1 text-xs text-muted-foreground truncate">{sub}</div>}
        </div>
    );
}

export default function SpaceStatsPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [space, setSpace] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: all }, { data: s }] = await Promise.all([
                    api.get('/spaces'),
                    api.get(`/spaces/${id}/stats`),
                ]);
                const sp = all.find((x) => x.id === id);
                setSpace(sp || null);
                setStats(s);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally { setLoading(false); }
        })();
    }, [id]);

    if (loading || !stats) {
        return (
            <div className="space-y-3">
                <Skeleton className="h-8 w-64 rounded-sm" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-28 rounded-sm" />
                    ))}
                </div>
                <Skeleton className="h-64 w-full rounded-sm" />
            </div>
        );
    }

    const pieData = (stats.file_type_distribution || []).map((d) => ({ name: d.type, value: d.count }));
    const barData = (stats.folder_sizes || []).map((d) => ({ folder: d.folder, size: d.size }));

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => nav(`/explorer/${id}`)} className="rounded-sm">
                    <ArrowLeft size={14} className="mr-1.5" /> Back to explorer
                </Button>
                {space && (
                    <div className="min-w-0">
                        <h1 className="font-display text-3xl font-black tracking-tight truncate">{space.name}</h1>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Storage Used" value={fmtBytes(stats.total_size)} icon={HardDrive} />
                <StatCard label="Total Files" value={stats.total_files.toLocaleString()} icon={FileStack} />
                <StatCard label="Total Folders" value={stats.total_folders} icon={Folder} />
                <StatCard label="Last Modified" value={fmtDate(stats.last_modified)} icon={Clock} sub="Newest change" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <StatCard
                    label="Largest file"
                    value={fmtBytes(stats.largest_file?.size || 0)}
                    icon={TrendingUp}
                    sub={stats.largest_file?.name}
                />
                <StatCard
                    label="Newest file"
                    value={stats.newest_file?.name || '—'}
                    icon={Star}
                    sub={fmtDate(stats.newest_file?.modified)}
                />
                <StatCard
                    label="Oldest file"
                    value={stats.oldest_file?.name || '—'}
                    icon={Clock}
                    sub={fmtDate(stats.oldest_file?.modified)}
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-sm border border-border bg-card p-5">
                    <div className="font-display text-lg font-bold mb-4">File types</div>
                    {pieData.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No data yet.</div>
                    ) : (
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label={(e) => e.name}>
                                        {pieData.map((_, i) => (
                                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <ReTooltip
                                        contentStyle={{
                                            backgroundColor: 'hsl(var(--popover))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: 4, fontFamily: 'JetBrains Mono, monospace',
                                            fontSize: 12,
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="rounded-sm border border-border bg-card p-5">
                    <div className="font-display text-lg font-bold mb-4">Folder sizes</div>
                    {barData.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No data yet.</div>
                    ) : (
                        <div style={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <BarChart data={barData}>
                                    <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="folder" tick={{ fontFamily: 'JetBrains Mono', fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                                    <YAxis
                                        tick={{ fontFamily: 'JetBrains Mono', fontSize: 10 }}
                                        stroke="hsl(var(--muted-foreground))"
                                        tickFormatter={(v) => fmtBytes(v)}
                                    />
                                    <ReTooltip
                                        contentStyle={{
                                            backgroundColor: 'hsl(var(--popover))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
                                        }}
                                        formatter={(v) => fmtBytes(v)}
                                    />
                                    <Bar dataKey="size" fill="#0044FF" radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            <div className="text-[11px] text-muted-foreground">
                Scanned {stats.scanned_pages} page(s) of the bucket. Very large buckets are sampled.
            </div>
        </div>
    );
}
